import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// 配置axios默认设置
axios.defaults.baseURL = API_BASE_URL;

// 创建axios响应拦截器来处理全局认证错误
let authStore; // 将在export后设置

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (authStore) {
      const status = error.response?.status;
      const message = error.response?.data?.message || '';
      // 401：未认证/过期 -> 直接清理
      if (status === 401) {
        authStore.getState().clearAuth();
        window.location.href = '/login';
      }
      // 403 且服务端返回“令牌无效” -> 视为认证失效
      if (status === 403 && typeof message === 'string' && message.includes('令牌无效')) {
        authStore.getState().clearAuth();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// 新增：请求拦截器，动态注入当前最新的Authorization头（函数级注释：在每次请求前从状态仓库读取token并设置到请求头，确保受保护接口正确携带令牌）
axios.interceptors.request.use(
  (config) => {
    try {
      const state = authStore?.getState?.();
      const token = state?.token;
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch (_) {
      // 忽略读取状态失败
    }
    return config;
  },
  (requestError) => Promise.reject(requestError)
);

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isInitialized: false,
      loading: false,

      // 设置认证信息
      setAuth: (user, token) => {
        set({ user, token, isAuthenticated: true, isInitialized: true });
        // 设置axios默认header
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      },

      // 清除认证信息
      clearAuth: () => {
        set({ user: null, token: null, isAuthenticated: false, isInitialized: true });
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem('auth-storage');
      },

      // 登录
      login: async (credentials) => {
        try {
          set({ loading: true });
          const response = await axios.post('/auth/login', credentials);
          
          if (response.data.success) {
            const { user, token } = response.data.data;
            get().setAuth(user, token);
            return { success: true };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          const message = error.response?.data?.message || '登录失败';
          return { success: false, message };
        } finally {
          set({ loading: false });
        }
      },

      // 注册
      register: async (userData) => {
        try {
          set({ loading: true });
          const response = await axios.post('/auth/register', userData);
          
          if (response.data.success) {
            const { user, token } = response.data.data;
            get().setAuth(user, token);
            return { success: true };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          const message = error.response?.data?.message || '注册失败';
          return { success: false, message };
        } finally {
          set({ loading: false });
        }
      },

      // 登出
      logout: () => {
        get().clearAuth();
      },

      // 获取当前用户信息
      getCurrentUser: async () => {
        try {
          const response = await axios.get('/auth/me');
          if (response.data.success) {
            set({ user: response.data.data.user, isInitialized: true });
            return response.data.data.user;
          }
        } catch (error) {
          console.error('获取用户信息失败:', error);
          if (error.response?.status === 401) {
            get().clearAuth();
          }
          throw error;
        }
      },

      // 更新用户资料
      updateProfile: async (profileData) => {
        try {
          set({ loading: true });
          const response = await axios.put('/auth/profile', profileData);
          
          if (response.data.success) {
            set({ user: response.data.data.user });
            return { success: true };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          const message = error.response?.data?.message || '更新失败';
          return { success: false, message };
        } finally {
          set({ loading: false });
        }
      },

      /**
       * 函数：changePassword 修改当前用户密码
       * 参数：{ currentPassword, newPassword }
       * 返回：{ success: boolean, message?: string }
       */
      changePassword: async ({ currentPassword, newPassword }) => {
        try {
          set({ loading: true });
          const response = await axios.post('/auth/change-password', {
            currentPassword,
            newPassword
          });
          if (response.data.success) {
            return { success: true };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          const message = error.response?.data?.message || '修改密码失败';
          return { success: false, message };
        } finally {
          set({ loading: false });
        }
      },

      /**
       * 函数：changeEmail 修改当前用户邮箱
       * 参数：{ newEmail, currentPassword }
       * 返回：{ success: boolean, message?: string }
       * 备注：成功后更新本地 user.email
       */
      changeEmail: async ({ newEmail, currentPassword }) => {
        try {
          set({ loading: true });
          const response = await axios.post('/auth/change-email', {
            newEmail,
            currentPassword
          });
          if (response.data.success) {
            const serverUser = response.data.data?.user;
            if (serverUser) {
              set({ user: serverUser });
            } else {
              // 后端未回传完整用户对象时，直接更新email字段
              const current = get().user;
              set({ user: { ...current, email: newEmail } });
            }
            return { success: true };
          } else {
            return { success: false, message: response.data.message };
          }
        } catch (error) {
          const message = error.response?.data?.message || '修改邮箱失败';
          return { success: false, message };
        } finally {
          set({ loading: false });
        }
      },

      // 初始化认证状态
      initializeAuth: async () => {
        const { token } = get();
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          try {
            await get().getCurrentUser();
          } catch (error) {
            // 如果token无效，清除认证信息
            console.warn('Token validation failed during initialization:', error);
            get().clearAuth();
          }
        } else {
          // 没有token时也标记为已初始化
          set({ isInitialized: true });
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

// 设置authStore引用用于拦截器
authStore = useAuthStore;

export { useAuthStore };
